"use client";

import Link from "@/components/ui/CcLink";
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type TransitionEvent,
} from "react";

type CcNavInlineDetailLabelProps = {
  label: string;
  /** The base label is its own nav link, so the rail keeps a stable wrapper. */
  labelHref?: string | null;
  /** Attach disclosure semantics to the focusable link, never the visual rail. */
  labelMenu?: { expanded: boolean; controls: string };
  onLabelClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  detail?: string | null;
  /** Optional middle segment before `detail` (e.g. Blog index on post routes). */
  detailLead?: string | null;
  /** Middle segment href (service hub, blog index, etc.). */
  detailLeadHref?: string | null;
  onSegmentNavigate?: () => void;
  /** List position directs swaps; unlisted or tied titles enter from below. */
  detailIndex?: number | null;
  /** Service-column position; directs both labels when changing hubs. */
  detailLeadIndex?: number | null;
  /** Parent-held detail text while route detail is cleared (survives remount during exit). */
  detailFallback?: string | null;
  /** SSR/hydration: detail already visible — skip width 0→full enter on first mount. */
  detailInitiallySettled?: boolean;
  /** True while detail text is shown or exit animation is running (for parent layout classes). */
  onDetailRailActiveChange?: (active: boolean) => void;
};

/** Extra px so measured width matches painted glyphs (see `$cc-nav-detail-reveal-mask-band` in SCSS). */
const DETAIL_CONTENT_WIDTH_SAFETY_PX = 1;

/** Matches `max-width` on `.cc-nav__link-label-detail` in `_navbar.scss`. */
const DETAIL_REVEAL_MS = 800;
/** Matches `--cc-nav-detail-mask-opacity` transition in `_navbar.scss`. */
const DETAIL_MASK_FADE_MS = 320;
/** Start mask fade before reveal ends so stages overlap (~last 170ms of reveal). */
const DETAIL_MASK_FADE_START_MS = DETAIL_REVEAL_MS - 170;
/** Start the collapse while the exit mask is still settling to avoid a visible pause. */
const DETAIL_COLLAPSE_WIDTH_START_MS = DETAIL_MASK_FADE_MS - 230;
/** Keep JS cleanup in step with the delayed CSS collapse animation. */
const DETAIL_COLLAPSE_COMPLETE_MS =
  DETAIL_COLLAPSE_WIDTH_START_MS + DETAIL_REVEAL_MS + 40;
/** Matches the secondary-button label swap in `_button.scss`. */
const DETAIL_SWAP_MS = 300;

type DetailSwap = {
  from: string;
  to: string;
  targetLead: string;
  direction: "down" | "up";
  /** Hierarchy changes slide the child and only swap the hub vertically. */
  hubOnly?: boolean;
  lead?: { from: string; to: string; fromWidth: number; toWidth: number };
};

type DetailSwapWidths = {
  from: number;
  to: number;
};

type DetailTrail = { detail: string; lead: string; leadHref: string };

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Parent nav label with optional " / Detail" suffix (width measured for smooth reveal). */
export function CcNavInlineDetailLabel({
  label,
  labelHref = null,
  labelMenu,
  onLabelClick,
  detail,
  detailLead = null,
  detailLeadHref = null,
  onSegmentNavigate,
  detailIndex = null,
  detailLeadIndex = null,
  detailFallback = null,
  detailInitiallySettled = false,
  onDetailRailActiveChange,
}: CcNavInlineDetailLabelProps) {
  const innerRef = useRef<HTMLSpanElement>(null);
  const leadCopyRef = useRef<HTMLSpanElement>(null);
  const currentCopyRef = useRef<HTMLSpanElement>(null);
  const previousLeadWidthRef = useRef(0);
  const contentWidthRef = useRef(0);
  const isRevealingRef = useRef(false);
  const isMaskFadingRef = useRef(false);
  const isCollapsingRef = useRef(false);
  const collapseInitRef = useRef(false);
  const revealMaskTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collapseWidthTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collapseCompleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isExitMaskFadingInRef = useRef(false);
  const lastDetailTextRef = useRef("");
  const previousDetailRef = useRef("");
  const previousDetailIndexRef = useRef<number | null>(null);
  const previousDetailLeadRef = useRef("");
  const previousLeadIndexRef = useRef<number | null>(null);
  const previousTrailRef = useRef<DetailTrail | null>(null);
  const detailSwapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hadDetailRef = useRef(false);
  /** After exit collapse finishes; prevents fallback prop from re-triggering exit. */
  const exitFinishedRef = useRef(false);
  const skipInitialEnterRevealRef = useRef(detailInitiallySettled);
  const [detailWidthPx, setDetailWidthPx] = useState(0);
  const [isRevealing, setIsRevealing] = useState(false);
  const [isMaskFading, setIsMaskFading] = useState(false);
  const [isMaskFadingOut, setIsMaskFadingOut] = useState(false);
  const [isExitMaskFadingIn, setIsExitMaskFadingIn] = useState(false);
  const [isExitMaskFadingInActive, setIsExitMaskFadingInActive] =
    useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [exitDetailText, setExitDetailText] = useState("");
  const [contentWidthPx, setContentWidthPx] = useState(0);
  const [detailSwap, setDetailSwap] = useState<DetailSwap | null>(null);
  const [detailSwapWidths, setDetailSwapWidths] =
    useState<DetailSwapWidths | null>(null);
  const [retainedTrail, setRetainedTrail] = useState<DetailTrail | null>(null);
  const [retiringTrail, setRetiringTrail] = useState<
    (DetailTrail & { target: string }) | null
  >(null);

  const trimmed = detail?.trim() ?? "";
  const detailLeadTrimmed = detailLead?.trim() ?? "";
  const labelHrefTrimmed = labelHref?.trim() ?? "";
  const detailLeadHrefTrimmed = detailLeadHref?.trim() ?? "";
  const fallbackTrimmed = detailFallback?.trim() ?? "";
  const hasDetail = trimmed.length > 0;
  const hasSegmentedDetail = hasDetail && detailLeadTrimmed.length > 0;

  const handleSegmentClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  };
  const combinedDetailText = hasSegmentedDetail
    ? `${detailLeadTrimmed} / ${trimmed}`
    : trimmed;
  const visibleSwap = detailSwap?.to === trimmed &&
    detailSwap.targetLead === detailLeadTrimmed ? detailSwap : null;
  const visibleCurrentSwap = visibleSwap?.hubOnly ? null : visibleSwap;
  const visibleRetiringTrail = retiringTrail?.target === combinedDetailText
    ? retiringTrail
    : null;
  // Keep the exact outgoing hierarchy and DOM shape until the clip collapses.
  const exitTrail = !hasDetail ? retainedTrail : null;
  const heldTrail = visibleRetiringTrail ?? exitTrail;
  const renderedLead = visibleRetiringTrail ? trimmed : heldTrail?.lead ?? detailLeadTrimmed;
  const renderedLeadHref = visibleRetiringTrail && visibleRetiringTrail.lead !== trimmed
    ? ""
    : heldTrail?.leadHref ?? detailLeadHrefTrimmed;
  const renderedDetail = heldTrail?.detail ?? trimmed;
  const renderSegmentedDetail = hasSegmentedDetail || Boolean(heldTrail?.lead);

  useLayoutEffect(() => {
    if (!retiringTrail) return;
    const timer = setTimeout(() => setRetiringTrail(null), DETAIL_REVEAL_MS + 40);
    return () => clearTimeout(timer);
  }, [retiringTrail]);

  if (hasDetail) {
    exitFinishedRef.current = false;
    lastDetailTextRef.current = combinedDetailText;
    hadDetailRef.current = true;
  } else if (fallbackTrimmed && !exitFinishedRef.current) {
    // Seed for exit only (remount mid-exit); never re-seed after collapse completes.
    lastDetailTextRef.current = exitTrail?.lead
      ? `${exitTrail.lead} / ${exitTrail.detail}`
      : fallbackTrimmed;
    hadDetailRef.current = true;
  }

  const exitDetailPending =
    !hasDetail &&
    !exitFinishedRef.current &&
    hadDetailRef.current &&
    lastDetailTextRef.current.length > 0;

  const showDetailRail =
    hasDetail ||
    isCollapsing ||
    isCollapsingRef.current ||
    exitDetailPending;
  const visibleDetailText = hasDetail
    ? combinedDetailText
    : exitDetailText || lastDetailTextRef.current || fallbackTrimmed;

  useLayoutEffect(() => {
    const previousDetail = previousDetailRef.current;
    const previousIndex = previousDetailIndexRef.current;
    const previousLead = previousDetailLeadRef.current;
    const previousLeadIndex = previousLeadIndexRef.current;
    // On a hub route the destination is in the current slot before the outgoing
    // child is retained. Measure it there for the middle label's width swap.
    const leadWidth = leadCopyRef.current?.scrollWidth ?? currentCopyRef.current?.scrollWidth ?? 0;
    // A hub occupies the current slot until a child gives it a middle slot.
    // Compare the hub identity independently of which slot currently holds it.
    const previousHub = previousLead || previousDetail;
    const nextHub = detailLeadTrimmed || trimmed;
    const hubOnly = Boolean(previousLead) !== Boolean(detailLeadTrimmed);
    const changesHub = Boolean(
      previousHub && nextHub && previousHub !== nextHub &&
      (previousLead || detailLeadTrimmed),
    );
    const changesSibling = previousLead === detailLeadTrimmed &&
      trimmed !== previousDetail;
    const fromIndex = changesHub ? previousLeadIndex : previousIndex;
    const toIndex = changesHub ? detailLeadIndex : detailIndex;

    if (detailSwapTimerRef.current !== null) {
      clearTimeout(detailSwapTimerRef.current);
      detailSwapTimerRef.current = null;
    }

    if (
      trimmed &&
      previousDetail &&
      (changesHub || changesSibling) &&
      !prefersReducedMotion()
    ) {
      setDetailSwap({
        from: previousDetail,
        to: trimmed,
        targetLead: detailLeadTrimmed,
        hubOnly,
        direction: fromIndex !== null && toIndex !== null && toIndex < fromIndex
          ? "up"
          : "down",
        lead: changesHub && (previousLead || detailLeadTrimmed) ? {
          from: previousHub,
          to: nextHub,
          fromWidth: previousLeadWidthRef.current,
          toWidth: leadWidth,
        } : undefined,
      });
      detailSwapTimerRef.current = setTimeout(() => {
        detailSwapTimerRef.current = null;
        setDetailSwap(null);
        setDetailSwapWidths(null);
      }, DETAIL_SWAP_MS);
    } else {
      setDetailSwap(null);
      setDetailSwapWidths(null);
    }

    if (trimmed) {
      previousDetailRef.current = trimmed;
      previousDetailIndexRef.current = detailIndex;
      previousDetailLeadRef.current = detailLeadTrimmed;
      previousLeadIndexRef.current = detailLeadIndex;
      previousLeadWidthRef.current = leadWidth;
    } else {
      previousDetailRef.current = "";
      previousDetailIndexRef.current = null;
      previousDetailLeadRef.current = "";
      previousLeadIndexRef.current = null;
      previousLeadWidthRef.current = 0;
    }

    return () => {
      if (detailSwapTimerRef.current !== null) {
        clearTimeout(detailSwapTimerRef.current);
        detailSwapTimerRef.current = null;
      }
    };
  }, [trimmed, detailIndex, detailLeadTrimmed, detailLeadIndex]);

  const finishExitCollapse = () => {
    if (collapseCompleteTimerRef.current !== null) {
      clearTimeout(collapseCompleteTimerRef.current);
      collapseCompleteTimerRef.current = null;
    }
    if (collapseWidthTimerRef.current !== null) {
      clearTimeout(collapseWidthTimerRef.current);
      collapseWidthTimerRef.current = null;
    }
    isCollapsingRef.current = false;
    collapseInitRef.current = false;
    isExitMaskFadingInRef.current = false;
    exitFinishedRef.current = true;
    previousTrailRef.current = null;
    setRetainedTrail(null);
    lastDetailTextRef.current = "";
    hadDetailRef.current = false;
    setIsExitMaskFadingIn(false);
    setIsExitMaskFadingInActive(false);
    setIsCollapsing(false);
    setExitDetailText("");
    setContentWidthPx(0);
    setDetailWidthPx(0);
  };

  useLayoutEffect(() => {
    if (!isMaskFading || prefersReducedMotion()) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      setIsMaskFadingOut(true);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [isMaskFading]);

  useLayoutEffect(() => {
    const inner = innerRef.current;
    const detailEl = inner?.parentElement;

    const clearRevealMaskTimer = () => {
      if (revealMaskTimerRef.current !== null) {
        clearTimeout(revealMaskTimerRef.current);
        revealMaskTimerRef.current = null;
      }
    };

    const clearCollapseWidthTimer = () => {
      if (collapseWidthTimerRef.current !== null) {
        clearTimeout(collapseWidthTimerRef.current);
        collapseWidthTimerRef.current = null;
      }
    };

    const clearCollapseCompleteTimer = () => {
      if (collapseCompleteTimerRef.current !== null) {
        clearTimeout(collapseCompleteTimerRef.current);
        collapseCompleteTimerRef.current = null;
      }
    };

    const clearExitMaskFadeIn = () => {
      isExitMaskFadingInRef.current = false;
      setIsExitMaskFadingIn(false);
      setIsExitMaskFadingInActive(false);
    };

    const startMaskFade = () => {
      if (prefersReducedMotion() || isMaskFadingRef.current) {
        return;
      }
      // End reveal stage before mask fade so inner text is not mid blur animation.
      isRevealingRef.current = false;
      setIsRevealing(false);
      isMaskFadingRef.current = true;
      setIsMaskFading(true);
      setIsMaskFadingOut(false);
    };

    const clearMaskFade = () => {
      clearRevealMaskTimer();
      isMaskFadingRef.current = false;
      setIsMaskFading(false);
      setIsMaskFadingOut(false);
    };

    if (hasDetail) {
      setRetiringTrail(null);
      exitFinishedRef.current = false;
      isCollapsingRef.current = false;
      collapseInitRef.current = false;
      clearCollapseWidthTimer();
      clearCollapseCompleteTimer();
      clearExitMaskFadeIn();
      setIsCollapsing(false);
      setExitDetailText("");
      clearMaskFade();
    }

    const pendingExitText =
      lastDetailTextRef.current || fallbackTrimmed || "";

    if (
      !hasDetail &&
      !exitFinishedRef.current &&
      !isCollapsing &&
      !isCollapsingRef.current &&
      pendingExitText
    ) {
      const previousDetail = pendingExitText;
      lastDetailTextRef.current = previousDetail;
      hadDetailRef.current = true;

      if (prefersReducedMotion()) {
        exitFinishedRef.current = true;
        previousTrailRef.current = null;
        setRetainedTrail(null);
        lastDetailTextRef.current = "";
        hadDetailRef.current = false;
        setExitDetailText("");
        setDetailWidthPx(0);
        setIsRevealing(false);
        isRevealingRef.current = false;
        clearMaskFade();
        return;
      }

      clearMaskFade();
      clearCollapseWidthTimer();
      clearExitMaskFadeIn();
      isRevealingRef.current = false;
      setIsRevealing(false);
      setExitDetailText(previousDetail);

      if (inner && detailEl instanceof HTMLElement) {
        // The non-shrinking inner exposes its full width while the rail clips it.
        // Clearing max-width would snap the rail to zero if React's width value
        // stayed unchanged and therefore did not get written back to the DOM.
        const width =
          Math.ceil(inner.scrollWidth) + DETAIL_CONTENT_WIDTH_SAFETY_PX;
        contentWidthRef.current = width;
        setContentWidthPx(width);
        setDetailWidthPx(width);
      }

      isCollapsingRef.current = true;
      collapseInitRef.current = true;
      setIsCollapsing(true);
      isExitMaskFadingInRef.current = true;
      setIsExitMaskFadingIn(true);
      setIsExitMaskFadingInActive(false);

      let exitMaskFrame = 0;
      const exitMaskFrame1 = requestAnimationFrame(() => {
        exitMaskFrame = requestAnimationFrame(() => {
          setIsExitMaskFadingInActive(true);
        });
      });

      collapseWidthTimerRef.current = setTimeout(() => {
        collapseWidthTimerRef.current = null;
        if (!isCollapsingRef.current) {
          return;
        }
        setDetailWidthPx(0);
      }, DETAIL_COLLAPSE_WIDTH_START_MS);

      collapseCompleteTimerRef.current = setTimeout(() => {
        if (!isCollapsingRef.current) {
          return;
        }
        finishExitCollapse();
      }, DETAIL_COLLAPSE_COMPLETE_MS);

      return () => {
        cancelAnimationFrame(exitMaskFrame1);
        cancelAnimationFrame(exitMaskFrame);
        // Keep exit timers when `isCollapsing` re-runs this effect mid-collapse.
        if (!isCollapsingRef.current) {
          clearCollapseWidthTimer();
          clearCollapseCompleteTimer();
        }
      };
    }

    if (isCollapsing || isCollapsingRef.current) {
      return;
    }

    if (!hasDetail || !inner || !(detailEl instanceof HTMLElement)) {
      if (
        exitDetailPending ||
        isCollapsing ||
        isCollapsingRef.current ||
        lastDetailTextRef.current.length > 0
      ) {
        return;
      }
      setDetailWidthPx(0);
      setContentWidthPx(0);
      setIsRevealing(false);
      isRevealingRef.current = false;
      clearMaskFade();
      return;
    }

    const measure = () => {
      // The inner content exposes its full scroll width even while clipped.
      // Changing the rail's max-width here would reset its running transition.
      return Math.ceil(inner.scrollWidth) + DETAIL_CONTENT_WIDTH_SAFETY_PX;
    };

    const previousContentWidth = contentWidthRef.current;
    const previousTrail = previousTrailRef.current;
    const contentWidth = measure();
    previousTrailRef.current = {
      detail: trimmed,
      lead: detailLeadTrimmed,
      leadHref: detailLeadHrefTrimmed,
    };
    setRetainedTrail(previousTrailRef.current);
    contentWidthRef.current = contentWidth;
    setContentWidthPx(contentWidth);

    if (prefersReducedMotion()) {
      setDetailWidthPx(contentWidth);
      setIsRevealing(false);
      isRevealingRef.current = false;
      clearMaskFade();
      return;
    }

    if (skipInitialEnterRevealRef.current) {
      skipInitialEnterRevealRef.current = false;
      previousDetailRef.current = trimmed;
      previousDetailIndexRef.current = detailIndex;
      clearMaskFade();
      isRevealingRef.current = false;
      setIsRevealing(false);
      setDetailWidthPx(contentWidth);
      return;
    }

    if (previousTrail) {
      // Navigation within an open trail moves directly from its current width.
      clearMaskFade();
      isRevealingRef.current = false;
      setIsRevealing(false);
      if (previousTrail.lead && !detailLeadTrimmed) {
        // Keep the old suffix painted while the right edge wipes back to the
        // destination hub. Its middle label can swap independently of the wipe.
        setRetiringTrail({ ...previousTrail, target: combinedDetailText });
      }
      setDetailSwapWidths({
        from: previousContentWidth || contentWidth,
        to: contentWidth,
      });
      setDetailWidthPx(contentWidth);
      return;
    }

    clearMaskFade();
    isRevealingRef.current = true;
    setIsRevealing(true);
    setDetailWidthPx(0);
    let revealFrame2 = 0;
    const revealFrame1 = requestAnimationFrame(() => {
      revealFrame2 = requestAnimationFrame(() => {
        setDetailWidthPx(contentWidth);
        clearRevealMaskTimer();
        revealMaskTimerRef.current = setTimeout(() => {
          revealMaskTimerRef.current = null;
          if (!isRevealingRef.current) {
            return;
          }
          startMaskFade();
        }, DETAIL_MASK_FADE_START_MS);
      });
    });

    return () => {
      cancelAnimationFrame(revealFrame1);
      cancelAnimationFrame(revealFrame2);
      clearRevealMaskTimer();
    };
    // The combined text tracks both hub and subpage changes. Animation state
    // updates must not rerun this measurement and restart the width transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDetail, combinedDetailText, isCollapsing, fallbackTrimmed]);

  useLayoutEffect(() => {
    onDetailRailActiveChange?.(showDetailRail);
  }, [onDetailRailActiveChange, showDetailRail]);

  const isCollapsingVisual = isCollapsing || isCollapsingRef.current;
  const usesTrailingMask =
    isRevealing ||
    isMaskFading ||
    isExitMaskFadingIn ||
    isCollapsingVisual;

  const detailClassName = [
    "cc-nav__link-label-detail",
    isRevealing ? "cc-nav__link-label-detail--revealing" : "",
    isMaskFading ? "cc-nav__link-label-detail--mask-fading" : "",
    isMaskFadingOut ? "cc-nav__link-label-detail--mask-fading-out" : "",
    isExitMaskFadingIn
      ? "cc-nav__link-label-detail--collapse-mask-fading-in"
      : "",
    isExitMaskFadingInActive
      ? "cc-nav__link-label-detail--collapse-mask-fading-in-active"
      : "",
    isCollapsingVisual ? "cc-nav__link-label-detail--collapsing" : "",
    visibleCurrentSwap ? "cc-nav__link-label-detail--swapping" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const detailMaskWidth =
    usesTrailingMask && contentWidthPx > 0
      ? ({
          "--cc-nav-detail-content-width": `${contentWidthPx}px`,
        } as CSSProperties)
      : undefined;
  const detailSwapWidthVars = detailSwapWidths
    ? ({
        "--cc-nav-detail-swap-from-width": `${detailSwapWidths.from}px`,
        "--cc-nav-detail-swap-to-width": `${detailSwapWidths.to}px`,
      } as CSSProperties)
    : undefined;

  const handleDetailTransitionEnd = (event: TransitionEvent<HTMLSpanElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.propertyName === "--cc-nav-detail-mask-opacity") {
      if (isExitMaskFadingInRef.current) {
        isExitMaskFadingInRef.current = false;
        setIsExitMaskFadingIn(false);
        setIsExitMaskFadingInActive(false);
        return;
      }
      if (!isMaskFadingRef.current) {
        return;
      }
      isMaskFadingRef.current = false;
      setIsMaskFading(false);
      setIsMaskFadingOut(false);
      return;
    }

    if (event.propertyName !== "max-width") {
      return;
    }

    if (isCollapsingRef.current) {
      finishExitCollapse();
      return;
    }

    if (!isRevealingRef.current) {
      return;
    }

    isRevealingRef.current = false;
    setIsRevealing(false);

    if (prefersReducedMotion()) {
      return;
    }

    if (!isMaskFadingRef.current) {
      isMaskFadingRef.current = true;
      setIsMaskFading(true);
      setIsMaskFadingOut(false);
    }
  };

  const labelBase = labelHrefTrimmed ? (
    <Link
      href={labelHrefTrimmed}
      priority="primary"
      className="cc-nav__link-label-base cc-nav__link-label-segment-link"
      aria-haspopup={labelMenu ? "menu" : undefined}
      aria-expanded={labelMenu?.expanded}
      aria-controls={labelMenu?.controls}
      onClick={(event) => {
        handleSegmentClick(event);
        onLabelClick?.(event);
      }}
      onNavigate={onSegmentNavigate}
    >
      {label}
    </Link>
  ) : (
    <span className="cc-nav__link-label-base">{label}</span>
  );

  const currentLabel = (
    <span
      ref={currentCopyRef}
      className={`cc-nav__link-label-current ${renderSegmentedDetail ? "cc-nav__link-label-current--ellipsis" : ""} ${visibleCurrentSwap ? `cc-nav__link-label-current--swapping cc-nav__link-label-current--swap-${visibleCurrentSwap.direction}` : ""}`}
    >
      {visibleCurrentSwap ? (
        <>
          <span className="cc-nav__link-label-current-slot cc-nav__link-label-current-slot--outgoing" aria-hidden="true">
            {visibleCurrentSwap.from}
          </span>
          <span className="cc-nav__link-label-current-slot cc-nav__link-label-current-slot--incoming">
            {visibleCurrentSwap.to}
          </span>
        </>
      ) : (
        renderSegmentedDetail ? renderedDetail : visibleDetailText
      )}
    </span>
  );

  const leadLabel = (
    <span
      ref={leadCopyRef}
      className={`cc-nav__link-label-lead-copy ${visibleSwap?.lead ? `cc-nav__link-label-current--swapping cc-nav__link-label-current--swap-${visibleSwap.direction}` : ""}`}
      style={visibleSwap?.lead ? ({
        "--cc-nav-lead-from-width": `${visibleSwap.lead.fromWidth}px`,
        "--cc-nav-lead-to-width": `${visibleSwap.lead.toWidth}px`,
      } as CSSProperties) : undefined}
    >
      {visibleSwap?.lead ? (
        <>
          <span className="cc-nav__link-label-current-slot cc-nav__link-label-current-slot--outgoing" aria-hidden="true">
            {visibleSwap.lead.from}
          </span>
          <span className="cc-nav__link-label-current-slot cc-nav__link-label-current-slot--incoming">
            {visibleSwap.lead.to}
          </span>
        </>
      ) : renderedLead}
    </span>
  );

  return (
    <span
      className={`cc-nav__link-label ${showDetailRail ? "cc-nav__link-label--has-detail" : ""}`}
    >
      {labelBase}
      <span
        className={detailClassName}
        aria-hidden={!showDetailRail}
        style={{
          maxWidth: showDetailRail ? detailWidthPx : 0,
          ...detailMaskWidth,
          ...detailSwapWidthVars,
        }}
        onTransitionEnd={handleDetailTransitionEnd}
      >
        <span
          ref={innerRef}
          className={`cc-nav__link-label-detail-inner ${renderSegmentedDetail ? "cc-nav__link-label-detail-inner--segmented" : ""}`}
        >
          <span className="cc-nav__link-label-sep" aria-hidden="true">
            /
          </span>
          {renderSegmentedDetail ? (
            <>
              {labelHrefTrimmed && renderedLeadHref ? (
                <Link
                  href={renderedLeadHref}
                  className="cc-nav__link-label-lead cc-nav__link-label-segment-link"
                  onClick={handleSegmentClick}
                  onNavigate={onSegmentNavigate}
                >
                  {leadLabel}
                </Link>
              ) : (
                <span className="cc-nav__link-label-lead">{leadLabel}</span>
              )}
              <span className="cc-nav__link-label-sep" aria-hidden="true">
                /
              </span>
              {currentLabel}
            </>
          ) : (
            currentLabel
          )}
        </span>
      </span>
    </span>
  );
}
